/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.13.0
 */

import { Fragment } from 'react';

import classNames from 'classnames';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { useSelector } from 'react-redux';

import { icons } from '../../../../../assets';
import { Collapse, CollapsePanel, EllipseText } from '../../../../../components/common';
import { ImgStyled } from '../../../../../components/common/Block/Img';
import { CollapseStandardStyled } from '../../../../../components/common/Collapse/styled/CollapseStyled';
import { useSubscriberIdContext } from '../../../context';
import { dcRuleActiveSelector, dcRulesTranslationSelector } from '../selectors';
import { EDcActiveRuleType, IDcRuleActiveItem } from '../types';
import { SubscriberIdDcRulesAccordionStyled } from './styled/SubscriberIdDcRulesStyled';

const SubscriberIdDcRuleAccordion = () => {

    const { subscriberId } = useSubscriberIdContext();

    const translationTab = useSelector(dcRulesTranslationSelector());
    const rules = useSelector(dcRuleActiveSelector(subscriberId));

    const renderAccordionTitleCell = (value?: string) => (
        <EllipseText lines={2} title={value}>
            {value || translationTab.rules.noValue}
        </EllipseText>
    );

    const renderAccordionIcon = (item: IDcRuleActiveItem) => {
        if (!item.type) return;
        const icon = {
            [EDcActiveRuleType.callCharges]: icons.subscriberRuleTariffIcon,
            [EDcActiveRuleType.productCharges]: icons.subscriberRuleProductIcon,
            [EDcActiveRuleType.otherCharges]: icons.blockIcon,
        }[item.type];

        return <ImgStyled className="subscriber-dc-rules-accordion__icon small" src={icon}/>;
    };

    const renderAccordionTitle = (item: IDcRuleActiveItem) => (
        <div className="subscriber-dc-rules-accordion__title">
            <div className="subscriber-dc-rules-accordion__name">
                {renderAccordionIcon(item)}
                {renderAccordionTitleCell(item.name)}
            </div>
            {
                !!Object.keys(pickBy(item.limit, identity)).length && (
                    <Fragment>
                        {renderAccordionTitleCell(item.limit.break?.toString())}
                        {renderAccordionTitleCell(item.limit.warn?.toString())}
                        {renderAccordionTitleCell(item.limit.measure)}
                    </Fragment>
                )
            }
        </div>
    );

    const renderRuleAccordion = (item: IDcRuleActiveItem, level = 0) => (
        <SubscriberIdDcRulesAccordionStyled
            className={classNames({ 'accordion_no-child': !item.child || !item.child.length })}
            key={item.name}
            level={level}
        >
            <CollapseStandardStyled style={{ marginTop: 0, width: level ? 'auto' : 'auto' }}>
                {
                    !!item.child.length
                        ? <Collapse icon={icons.arrowListDownMedium}>
                            <CollapsePanel isOpen titleValue={renderAccordionTitle(item)}>
                                {
                                    item.child.map(child => renderRuleAccordion(child, level + 1))
                                }
                            </CollapsePanel>
                        </Collapse>
                        : <CollapsePanel isOpen={false} titleValue={renderAccordionTitle(item)}/>
                }
            </CollapseStandardStyled>
        </SubscriberIdDcRulesAccordionStyled>
    );

    if (!rules?.length) return null;

    return (
        <div className="subscriber-dc-rules__accordion-list">
            <div className="subscriber-dc-rules-accordion__head">
                <div>{renderAccordionTitleCell(translationTab.rules.name)}</div>
                <div>{renderAccordionTitleCell(translationTab.rules.break)}</div>
                <div>{renderAccordionTitleCell(translationTab.rules.warn)}</div>
                <div>{renderAccordionTitleCell(translationTab.rules.measure)}</div>
            </div>
            {
                rules.map(item => renderRuleAccordion(item, 0))
            }
        </div>
    );
};

export default SubscriberIdDcRuleAccordion;
